// src/Components/TerminalForm.js
import { memo, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import api from '../api/api.js';
import { terminalSchema } from '../lib/validations';
import Button from './ui/Button.jsx';
import { useToast } from './ui/Toast.jsx';
import f from '../styles/forms.module.css';

const blockNonDigits = (e) => {
  const nav = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
  if (!nav.includes(e.key) && !/^[0-9]$/.test(e.key)) e.preventDefault();
};

const TerminalForm = memo(({ form, resetForm, onSuccess }) => {
  const isEditing = !!form.id;
  const { showToast } = useToast();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(terminalSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      name:       form.name       || '',
      address:    form.address    || '',
      tel_number: form.tel_number || '',
    },
  });

  useEffect(() => {
    reset({
      name:       form.name       || '',
      address:    form.address    || '',
      tel_number: form.tel_number || '',
    });
  }, [form.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const [serverError, setServerError] = useState('');

  const onSubmit = async (data) => {
    setServerError('');
    const payload = {
      name:       data.name.trim(),
      address:    data.address.trim(),
      tel_number: data.tel_number,
    };

    try {
      if (isEditing) {
        await api.put(`/terminal/${form.id}`, payload);
        onSuccess({ ...payload, id: form.id });
      } else {
        const res = await api.post('/terminal', payload);
        const newTerminal = res.data.id
          ? res.data
          : { id: res.data.result?.insertId, ...payload };
        if (!newTerminal.id) throw new Error('El servidor no devolvió el ID de la terminal');
        onSuccess(newTerminal);
      }

      showToast(`Terminal ${isEditing ? 'actualizada' : 'agregada'} correctamente`, { type: 'success' });
      resetForm();
    } catch (err) {
      setServerError(
        err.response?.data?.errors?.[0]?.message ||
        err.response?.data?.error ||
        err.message ||
        'Hubo un error al guardar la terminal.'
      );
    }
  };

  return (
    <div className={f.card}>
      {/* Header */}
      <div className={f.cardHeader}>
        <div className={f.cardTitleGroup}>
          <h3 className={f.cardTitle}>{isEditing ? 'Editar Terminal' : 'Nueva Terminal'}</h3>
          <p className={f.cardDesc}>
            {isEditing
              ? 'Actualiza la información de la terminal seleccionada.'
              : 'Registra una nueva terminal de autobuses en el sistema.'}
          </p>
        </div>
        <span className={f.badgeAdmin}>Admin</span>
      </div>
      <hr className={f.divider} />

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        {serverError && <div className={f.serverError} role="alert">{serverError}</div>}

        <div className={f.section}>
          <span className={f.sectionLabel}>Datos de la Terminal</span>
          <div className={f.grid2}>
            {/* Nombre */}
            <div className={f.field}>
              <label htmlFor="term-name" className={`${f.label} ${f.required}`}>Nombre</label>
              <input
                id="term-name"
                placeholder="Ej: Terminal La Bandera"
                className={`${f.input} ${errors.name ? f.inputError : ''}`}
                aria-describedby={errors.name ? 'term-name-error' : undefined}
                aria-invalid={!!errors.name}
                {...register('name')}
              />
              {errors.name && <span id="term-name-error" className={f.fieldError} role="alert">{errors.name.message}</span>}
            </div>

            {/* Teléfono */}
            <div className={f.field}>
              <label htmlFor="term-phone" className={`${f.label} ${f.required}`}>Teléfono</label>
              <input
                id="term-phone"
                placeholder="Solo dígitos"
                inputMode="numeric"
                className={`${f.input} ${errors.tel_number ? f.inputError : ''}`}
                aria-describedby={errors.tel_number ? 'term-phone-error' : undefined}
                aria-invalid={!!errors.tel_number}
                onKeyDown={blockNonDigits}
                {...register('tel_number')}
              />
              {errors.tel_number && <span id="term-phone-error" className={f.fieldError} role="alert">{errors.tel_number.message}</span>}
            </div>

            {/* Dirección */}
            <div className={`${f.field} ${f.full}`}>
              <label htmlFor="term-addr" className={`${f.label} ${f.required}`}>Dirección</label>
              <input
                id="term-addr"
                placeholder="Ej: Av. Principal con Calle 5, Sector Centro"
                className={`${f.input} ${errors.address ? f.inputError : ''}`}
                aria-describedby={errors.address ? 'term-addr-error' : undefined}
                aria-invalid={!!errors.address}
                {...register('address')}
              />
              {errors.address && <span id="term-addr-error" className={f.fieldError} role="alert">{errors.address.message}</span>}
            </div>
          </div>
        </div>

        {/* ── Acciones ── */}
        <div className={f.actions}>
          <Button type="submit" variant="primary" loading={isSubmitting}>
            {isEditing ? 'Actualizar Terminal' : 'Registrar Terminal'}
          </Button>
          {isEditing && (
            <Button type="button" variant="secondary" onClick={resetForm}>
              Cancelar
            </Button>
          )}
        </div>
      </form>
    </div>
  );
});

export default TerminalForm;
